import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useDownloadDocument } from '@kanda-libs/ks-component-ts';
import { useToast } from '@kanda-libs/ks-design-library';
import { actions } from '@kanda-libs/ks-frontend-services';

import {
  type FormattedSatNoteData,
  formatSatNoteData,
} from 'components/job/JobLayout/JobSatNote/helpers';
import { useAppDispatch } from 'components/shared/App';
import useApiError from 'hooks/useApiError';
import useReloadInitialData from 'hooks/useReloadInitialData';
import { selectors } from 'store';
import jobSelectors from 'store/selectors/job';

export interface JobSatNotePropsHook extends FormattedSatNoteData {
  showContent: boolean;
  showApprove: boolean;
  onClick: () => void;
  onApproveClick: () => void;
  onDelayClick: () => void;
  onWithLenderClick: () => void;
  isSubmitting: boolean;
  isLoading: boolean;
  showSendToLender: boolean;
}

export default function useJobSatNoteProps(): JobSatNotePropsHook {
  const dispatch = useAppDispatch();
  const isLoading = useSelector(selectors.getIsLoading);
  const job = useSelector(jobSelectors.getEntity);
  const handleApiError = useApiError('Error encountered with updating job');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const provider = useMemo(() => {
    if (!job) return undefined;
    return job?.customer_options?.finance_option?.provider;
  }, [job]);

  const showSendToLender = useMemo(() => {
    if (!provider) return false;
    return provider === 'propensio';
  }, [provider]);

  const refreshAll = useReloadInitialData();

  const { showError, showSuccess } = useToast();

  const { downloadDocument, isSubmitting: isDownloading = false } =
    useDownloadDocument();

  const showContent = useMemo(() => {
    if (!job?.sat_note) return false;
    return true;
  }, [job]);

  const showApprove = useMemo(() => {
    if (
      ['sat_note_signed', 'sat_note_delayed', 'sat_note_lender_review'].indexOf(
        job?.finance_status || '',
      ) === -1
    )
      return false;
    return true;
  }, [job]);

  const details = formatSatNoteData(job);

  const onClick = useCallback(() => {
    if (!details?.certificate?.id) return;
    downloadDocument(details?.certificate?.id);
  }, [details, downloadDocument]);

  const onApproveClick = useCallback(() => {
    setIsSubmitting(true);
    dispatch(
      actions.approveJobSatNote({
        params: { id: job?.id as string },
        onError: (err) => {
          handleApiError(err);
          setIsSubmitting(false);
        },
        onSuccess: () => {
          showSuccess('Sat note approved');
          setIsSubmitting(false);
        },
      }),
    );
  }, [showError, refreshAll, job, handleApiError]);

  const onDelayClick = useCallback(() => {
    setIsSubmitting(true);
    dispatch(
      actions.delayJobSatNote({
        params: { id: job?.id as string },
        onError: (err) => {
          handleApiError(err);
          setIsSubmitting(false);
        },
        onSuccess: () => {
          showSuccess('Sat note delayed');
          setIsSubmitting(false);
        },
      }),
    );
  }, [showError, refreshAll, job, handleApiError]);

  const onWithLenderClick = useCallback(() => {
    setIsSubmitting(true);
    dispatch(
      actions.lenderReviewJobSatNote({
        params: { id: job?.id as string },
        onError: (err) => {
          handleApiError(err);
          setIsSubmitting(false);
        },
        onSuccess: () => {
          showSuccess('Sat note delayed');
          setIsSubmitting(false);
        },
      }),
    );
  }, [showError, refreshAll, job, handleApiError]);

  return {
    showContent,
    showApprove,
    onClick,
    onApproveClick,
    onDelayClick,
    onWithLenderClick,
    isSubmitting,
    isLoading,
    showSendToLender,
    ...details,
  };
}
